import React, { useState } from "react";

import {
  Info as InfoIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

interface Tool {
  name: string;
  cost: number;
  url: string;
  total?: boolean;
  min?: number;
  description?: string;
  id: string;
}

const tools: Tool[] = [
  {
    id: "slack",
    name: "Slack",
    cost: 12.5,
    url: "https://slack.com/pricing",
    description: "Team communication and collaboration",
  },
  {
    id: "github",
    name: "GitHub",
    cost: 19.25,
    url: "https://github.com/pricing",
    description: "Code repository and CI/CD pipelines",
  },
  {
    id: "jira",
    name: "Jira",
    cost: 15.25,
    url: "https://www.atlassian.com/software/jira/pricing",
    description: "Project and issue tracking",
  },
  {
    id: "confluence",
    name: "Confluence",
    cost: 11,
    url: "https://www.atlassian.com/software/confluence/pricing",
    description: "Team knowledge base and documentation",
  },
  {
    id: "pagerduty",
    name: "PagerDuty",
    cost: 41,
    url: "https://www.pagerduty.com/pricing/",
    description: "Incident response and on-call management",
  },
  {
    id: "datadog",
    name: "Datadog",
    cost: 1500,
    total: true,
    url: "https://www.datadoghq.com/pricing/",
    description: "Monitoring and analytics platform",
  },
  {
    id: "okta",
    name: "Okta",
    cost: 15,
    min: 1500,
    url: "https://www.okta.com/pricing/",
    description: "Identity and access management",
  },
];

export interface ReplacesProps {
  orgSize: number;
  setOrgSize: (size: number) => void;
  onSavingsChange?: (savings: number) => void;
}

export function Replaces({
  orgSize,
  setOrgSize,
  onSavingsChange,
}: ReplacesProps) {
  // Initialize all tools as selected
  const [selectedTools, setSelectedTools] = useState<Record<string, boolean>>(
    tools.reduce((acc, tool) => ({ ...acc, [tool.id]: true }), {}),
  );

  const [customOrgSize, setCustomOrgSize] = useState<string>(
    orgSize.toString(),
  );
  const [isCustomSize, setIsCustomSize] = useState<boolean>(false);

  const toggleTool = (toolId: string) => {
    setSelectedTools((prev) => {
      const newSelection = {
        ...prev,
        [toolId]: !prev[toolId],
      };

      // Calculate new savings and notify parent
      const newFilteredTools = tools.filter((tool) => newSelection[tool.id]);
      const newSavings = calculateTotalPrice(newFilteredTools, orgSize);
      if (onSavingsChange) {
        onSavingsChange(newSavings);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const allSelected = tools.every((tool) => selectedTools[tool.id]);
    const newSelection = tools.reduce(
      (acc, tool) => ({
        ...acc,
        [tool.id]: !allSelected,
      }),
      {},
    );

    setSelectedTools(newSelection);

    // Calculate new savings and notify parent
    const newFilteredTools = tools.filter((tool) => !allSelected);
    const newSavings = calculateTotalPrice(newFilteredTools, orgSize);
    if (onSavingsChange) {
      onSavingsChange(newSavings);
    }
  };

  const handleOrgSizeChange = (
    _: React.ChangeEvent<{}> | Event,
    value: number | number[],
  ) => {
    const newSize = typeof value === "number" ? value : value[0];
    setOrgSize(newSize);
    setCustomOrgSize(newSize.toString());
    setIsCustomSize(false);
  };

  const handleCustomSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomOrgSize(value);

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue > 0) {
      setOrgSize(numValue);
      setIsCustomSize(numValue > 200);
    }
  };

  // Calculate savings whenever tools or orgSize changes
  React.useEffect(() => {
    const filteredTools = tools.filter((tool) => selectedTools[tool.id]);
    const savings = calculateTotalPrice(filteredTools, orgSize);
    if (onSavingsChange) {
      onSavingsChange(savings);
    }
  }, [selectedTools, orgSize, onSavingsChange]);

  const selectedCount = Object.values(selectedTools).filter(Boolean).length;
  const allSelected = selectedCount === tools.length;
  const someSelected = selectedCount > 0 && selectedCount < tools.length;

  const filteredTools = tools.filter((tool) => selectedTools[tool.id]);
  const totalSavings = calculateTotalPrice(filteredTools, orgSize);

  return (
    <>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Organization Size
        </Typography>

        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ px: 2 }}>
              <Slider
                value={!isCustomSize ? orgSize : 200}
                onChange={handleOrgSizeChange}
                step={10}
                marks={[
                  { value: 10, label: "10" },
                  { value: 50, label: "50" },
                  { value: 100, label: "100" },
                  { value: 200, label: "200+" },
                ]}
                min={10}
                max={200}
                disabled={isCustomSize}
                valueLabelDisplay="auto"
                sx={{
                  color: "primary.main",
                  "& .MuiSlider-thumb": {
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0 0 0 8px rgba(67, 211, 255, 0.16)",
                    },
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Organization Size"
                value={customOrgSize}
                onChange={handleCustomSizeChange}
                type="number"
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 2, minWidth: 80 }}
              >
                employees
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Tools Replaced by Microcumulus
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {allSelected ? "Deselect All" : "Select All"}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({selectedCount} of {tools.length} selected)
              </Typography>
            </Typography>
          }
        />
      </Box>

      <Grid container spacing={4}>
        {tools.map((tool, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <ToolCard
              tool={tool}
              orgSize={orgSize}
              isSelected={selectedTools[tool.id]}
              onToggle={() => toggleTool(tool.id)}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "rgba(70, 255, 147, 0.05)",
              border: "1px solid",
              borderColor: "rgba(70, 255, 147, 0.2)",
              textAlign: "center",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Total Tool Savings:
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(totalSavings)}
                <Typography
                  component="span"
                  variant="h6"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  per year
                </Typography>
              </Typography>
            </Stack>
            {selectedCount === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Select tools above to calculate your potential savings
              </Typography>
            )}
            {selectedCount > 0 && selectedCount < tools.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Based on {selectedCount} selected tools for an organization with{" "}
                {orgSize} employees
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

interface ToolCardProps {
  tool: Tool;
  orgSize: number;
  isSelected: boolean;
  onToggle: () => void;
}

function ToolCard({ tool, orgSize, isSelected, onToggle }: ToolCardProps) {
  const cost = calculateCost(orgSize, tool);

  const annualCost = cost * 12;

  const formattedCost = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cost);

  const formattedAnnualCost = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(annualCost);

  let costDetails;
  if (tool.total) {
    costDetails = `${formattedCost} flat rate per month`;
  } else if (tool.min && cost === tool.min) {
    costDetails = `${formattedCost} minimum monthly cost`;
  } else {
    costDetails = `${orgSize} × $${tool.cost.toFixed(2)} per month`;
  }

  return (
    <Card
      elevation={0}
      onClick={onToggle}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: isSelected ? "primary.main" : "divider",
        transition: "all 0.3s ease",
        backgroundColor: isSelected
          ? "rgba(67, 211, 255, 0.03)"
          : "background.paper",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          borderColor: "primary.main",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={isSelected}
                onChange={onToggle}
                onClick={(e) => e.stopPropagation()}
                color="primary"
                sx={{ ml: -1 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {tool.name}
              </Typography>
            </Box>
            <Tooltip title="View pricing details">
              <Link
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex" }}
                onClick={(e) => e.stopPropagation()}
              >
                <InfoIcon color="primary" />
              </Link>
            </Tooltip>
          </Stack>

          {tool.description && (
            <Typography variant="body2" color="text.secondary">
              {tool.description}
            </Typography>
          )}

          <Box sx={{ pt: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: isSelected ? "secondary.main" : "text.primary",
                mb: 1,
                transition: "color 0.3s ease",
              }}
            >
              {formattedCost}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                /month
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {costDetails}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formattedAnnualCost} per year
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <CheckIcon sx={{ color: "success.main", mr: 1, fontSize: 20 }} />
            <Typography variant="body2">Included with Microcumulus</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function totalPrice(orgSize: number): number {
  return calculateTotalPrice(tools, orgSize);
}

function calculateTotalPrice(toolList: Tool[], orgSize: number): number {
  return (
    toolList.reduce((acc, tool) => acc + calculateCost(orgSize, tool), 0) * 12
  ); // Annual cost
}

function calculateCost(orgSize: number, tool: Tool): number {
  if (tool.total) {
    return tool.cost;
  }

  const calculatedCost = orgSize * tool.cost;
  if (tool.min && calculatedCost < tool.min) {
    return tool.min;
  }

  return calculatedCost;
}
